import type { PhaseSlot } from '../types/project.types'

/**
 * AP-36 F — die Werte eines Phasen-Slots für den Speicher-Baustein.
 *
 * Der Endpunkt ist ein `PUT`: Er ersetzt den Slot als Ganzes, und was nicht
 * mitkommt, wird geleert. Deshalb geht bei jeder Feldänderung der **vollständige**
 * Slot hinaus; der Patch des Bausteins legt sich nur darüber. Projekt und
 * Position teilen sich diese Form — der Unterschied liegt allein darin, welche
 * Zeile der Antwort den eigenen Stand trägt.
 */
export interface SlotWerte {
  responsibleUserId: string
  startsAt: string
  endsAt: string
  isFixed: boolean
  fixedTime: string
  note: string
}

export function slotWerte(slot: PhaseSlot | null | undefined): SlotWerte {
  return {
    responsibleUserId: slot?.responsibleUserId ?? '',
    startsAt: slot?.startsAt ? String(slot.startsAt).slice(0, 10) : '',
    endsAt: slot?.endsAt ? String(slot.endsAt).slice(0, 10) : '',
    isFixed: Boolean(slot?.isFixed),
    fixedTime: slot?.fixedTime ?? '',
    note: slot?.note ?? '',
  }
}

/** Dieselben Werte plus dem Stand, gegen den geschrieben wird (AP-36 8.1). */
export function slotDaten(slot: PhaseSlot | null | undefined): Record<string, unknown> {
  return { ...slotWerte(slot), updatedAt: slot?.updatedAt }
}

/**
 * Aus einer Antwort des Slot-Endpunkts den **einen** Slot herausholen, den diese
 * Stelle schreibt.
 *
 * Sie bekommt zwei Formen zu sehen: die ganze Liste (Antwort des `PUT` und
 * `aktuell` im 409) und den bereits flachen Satz (die `daten`-Prop, die
 * `uebernimm` durch dieselbe Zuordnung schickt). Ohne diese Fallunterscheidung
 * verglich der Baustein die Felder nach einem Konflikt gegen eine Liste — und
 * fragte bei jedem 409 nach, statt still neu zu senden.
 *
 * `eigener` unterscheidet Projekt und Position: Am Projekt trägt `effective` den
 * Slot, an der Position ist nur `own` die eigene Abweichung.
 */
export function slotAusAntwort(antwort: unknown, phaseId: string, eigener: boolean): Record<string, unknown> {
  // Der Körper des 409 trägt den Datensatz in GET-Form, also in `data`; die
  // Antwort des `PUT` kommt aus der API-Datei schon ausgepackt.
  const roh = antwort as { data?: unknown } | null
  const innen = roh && typeof roh === 'object' && roh.data && typeof roh.data === 'object' ? roh.data : roh
  const liste = (innen as { slots?: { phaseId: string; own?: PhaseSlot | null; effective?: PhaseSlot | null }[] } | null)?.slots
  if (!liste) return (innen ?? {}) as Record<string, unknown>
  const eintrag = liste.find((s) => s.phaseId === phaseId)
  return slotDaten(eigener ? eintrag?.own : eintrag?.effective)
}

/**
 * Der Satz, der hinausgeht: die **Basis der Speicherstelle** (nicht die zuletzt
 * geladenen Props) plus der Patch.
 *
 * Nach einem 409 hat die Stelle die Werte des Servers übernommen; nur wer von
 * dort liest, schickt beim stillen zweiten Versuch nicht die alten Werte der
 * Felder zurück, die inzwischen ein anderer geändert hat. Der `PUT` ersetzt den
 * Slot als Ganzes — ohne das wäre das stille Neusenden ein stilles Überschreiben.
 */
export function slotAusBasis(basis: Record<string, unknown>, patch: Record<string, unknown>): SlotWerte {
  const zusammen = { ...basis, ...patch } as Record<string, unknown>
  return {
    responsibleUserId: (zusammen.responsibleUserId as string) ?? '',
    startsAt: zusammen.startsAt ? String(zusammen.startsAt).slice(0, 10) : '',
    endsAt: zusammen.endsAt ? String(zusammen.endsAt).slice(0, 10) : '',
    isFixed: Boolean(zusammen.isFixed),
    fixedTime: (zusammen.fixedTime as string) ?? '',
    note: (zusammen.note as string) ?? '',
  }
}
